import {
    findPredefinedSheetMetallThicknessIndex,
    findPredefinedCladdingDepthIndex,
    predefinedCladdingDepths,
} from "@dextall/panels-defaults";
import { CladdingCellStyle, GrainDirection } from "../../../../../responses/panelSource";
import colorsFactory from "../../../colors/ralColorsFactory";
import { Corner } from "../../../panels/corner";
import { WallCornerCladdingCell } from "../../../panels/wallCornerCladdingCell";
import { WallPanelCladdingCell } from "../../../panels/wallPanelCladdingCell";

export type CladdingCell2D = {
    box: THREE.Box3;
    color: string;
    material: string;
    titleColor: string;
};

export const createCornerRightWingCladdingCells = (panel: Corner): CladdingCell2D[] => {
    const straightCladdings = panel.isReversed ? panel.leftWingCladdings : panel.rightWingCladdings;

    const straightCells: CladdingCell2D[] = straightCladdings.map(x => {
        return {
            box: x.box,
            color: claddingCellToHtmlColor(x.color),
            material: getCladdingCellMaterial(x, x.sheetMetalThickness, x.depth),
            titleColor: x.titleColor,
        };
    });

    const cornerCells: CladdingCell2D[] = panel.cornerCladdings.map(x => {
        return panel.isReversed
            ? {
                box: x.leftBox,
                color: claddingCellToHtmlColor(x.leftWingColor),
                material: getCladdingCellMaterial(x, x.leftWingSheetMetalThickness, x.leftWingDepth),
                titleColor: x.leftWingTitleColor,
            }
            : {
                box: x.rightBox,
                color: claddingCellToHtmlColor(x.rightWingColor),
                material: getCladdingCellMaterial(x, x.rightWingSheetMetalThickness, x.rightWingDepth),
                titleColor: x.rightWingTitleColor,
            };
    });

    return straightCells.concat(cornerCells);
};

export const createCornerLeftWingCladdingCells = (panel: Corner): CladdingCell2D[] => {
    const wingLength = panel.isReversed ? panel.rightWing : panel.leftWing;

    const straightCladdings = panel.isReversed ? panel.rightWingCladdings : panel.leftWingCladdings;

    const straightCells: CladdingCell2D[] = straightCladdings.map(x => {
        return {
            box: getCornerWing2DCladdingBox(wingLength, x.box),
            color: claddingCellToHtmlColor(x.color),
            material: getCladdingCellMaterial(x, x.sheetMetalThickness, x.depth),
            titleColor: x.titleColor,
        };
    });

    const cornerCells: CladdingCell2D[] = panel.cornerCladdings.map(x => {
        return panel.isReversed
            ? {
                box: getCornerWing2DCladdingBox(wingLength, x.rightBox),
                color: claddingCellToHtmlColor(x.rightWingColor),
                material: getCladdingCellMaterial(x, x.rightWingSheetMetalThickness, x.rightWingDepth),
                titleColor: x.rightWingTitleColor,
            }
            : {
                box: getCornerWing2DCladdingBox(wingLength, x.leftBox),
                color: claddingCellToHtmlColor(x.leftWingColor),
                material: getCladdingCellMaterial(x, x.leftWingSheetMetalThickness, x.leftWingDepth),
                titleColor: x.leftWingTitleColor,
            };
    });

    return straightCells.concat(cornerCells);
};

const getCornerWing2DCladdingBox = (wingLength: number, box: THREE.Box3) => {
    return new THREE.Box3(
        new THREE.Vector3(wingLength - box.max.x, box.min.y, 0),
        new THREE.Vector3(wingLength - box.min.x, box.max.y, 0),
    );
};

const getCladdingCellMaterial = (
    cell: WallPanelCladdingCell | WallCornerCladdingCell,
    sheetMetalThickness: number,
    depth: number,
): string => {
    if (cell.style === CladdingCellStyle.Acm) {
        return `${cell.grainDirection === GrainDirection.Vertical ? upArrowSymbol : rightArrowSymbol} ACM`;
    }

    const defaultSheetMetalThicknessIndex = findPredefinedSheetMetallThicknessIndex(sheetMetalThickness);

    const defaultSheetMetalDepthIndex = findPredefinedCladdingDepthIndex(depth);

    const claddingDepth = predefinedCladdingDepths[defaultSheetMetalDepthIndex].name;

    switch (defaultSheetMetalThicknessIndex) {
        case 0:
            return `AL2 - ${claddingDepth}`;

        case 1:
            return `AL3 - ${claddingDepth}`;

        default:
            throw new Error("Invalid state");
    }
};

const claddingCellToHtmlColor = (cellColor: string) => {
    return colorsFactory.getColor(cellColor) || fallbackColor;
};

const upArrowSymbol = "\u2191";
const rightArrowSymbol = "\u2192";
const fallbackColor = "#FFFFFF";
